import { useMutation, useQueryClient } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type {
  PatchFormsId200,
  PatchFormsIdMutationRequest,
  PostForms201,
  PostFormsIdDuplicate201,
  PostFormsMutationRequest,
} from '@eureca/recruiter-api-sdk/types';
import { formByIdQueryKey } from './forms-query';

export class FormMutationError extends Error {
  readonly name = 'FormMutationError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

const FORMS_LIST_KEY = ['recruiter', 'forms'] as const;

export function usePostForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PostFormsMutationRequest): Promise<PostForms201> => {
      const res = await fetchClient<PostForms201>({
        method: 'POST',
        url: '/forms',
        data: input,
      });
      if (res.error) {
        throw new FormMutationError(
          `Failed to create form (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FORMS_LIST_KEY });
    },
  });
}

export function usePatchForm(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PatchFormsIdMutationRequest): Promise<PatchFormsId200> => {
      const res = await fetchClient<PatchFormsId200>({
        method: 'PATCH',
        url: `/forms/${formId}`,
        data: patch,
      });
      if (res.error) {
        throw new FormMutationError(
          `Failed to update form (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(formByIdQueryKey(formId), data);
      void queryClient.invalidateQueries({ queryKey: FORMS_LIST_KEY });
    },
  });
}

export function useDeleteForm(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await fetchClient<undefined>({
        method: 'DELETE',
        url: `/forms/${formId}`,
      });
      if (res.error) {
        throw new FormMutationError(
          `Failed to delete form (status ${res.error.status})`,
          res.error.status
        );
      }
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: formByIdQueryKey(formId) });
      void queryClient.invalidateQueries({ queryKey: FORMS_LIST_KEY });
    },
  });
}

export interface UploadFormAssetResponse {
  url: string;
}

/**
 * Upload a form asset (image used inside the SurveyJS schema) to the BFF
 * (`POST /forms/:id/assets`). The BFF writes the file to the public bucket
 * under `forms/<id>/assets/<uuid>.<ext>` and returns the resolved public URL,
 * which the SurveyJS Creator embeds in `imageLink` instead of base64.
 */
export async function uploadFormAsset(
  formId: string,
  file: File
): Promise<UploadFormAssetResponse> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const res = await fetchClient<UploadFormAssetResponse>({
    method: 'POST',
    url: `/forms/${formId}/assets`,
    data: formData,
  });
  if (res.error) {
    throw new FormMutationError(
      `Failed to upload form asset (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

/**
 * Duplicate mutation. Receives the source `id` per call (since the kebab
 * action target is row-scoped, not page-scoped). Returns the cloned row so
 * the caller can navigate straight into its editor.
 */
export function useDuplicateForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }): Promise<PostFormsIdDuplicate201> => {
      const res = await fetchClient<PostFormsIdDuplicate201>({
        method: 'POST',
        url: `/forms/${input.id}/duplicate`,
      });
      if (res.error) {
        throw new FormMutationError(
          `Failed to duplicate form (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FORMS_LIST_KEY });
    },
  });
}
